<script lang="ts" setup>
  import groupBy from 'lodash-es/groupBy'

  import lv from 'dayjs/locale/lv'
  import lt from 'dayjs/locale/lt'
  import en from 'dayjs/locale/en'
  import et from 'dayjs/locale/et'

  import type { InvestorEvent, IEventData } from '@/types/Storyblok/General/EventCard'

  const props = defineProps<{
    events: any
  }>()

  const emit = defineEmits(['open'])

  const { locale } = useI18n()
  const dayjs = useDayjs()
  const dayjsLocaleMapping = {
    lv,
    lt,
    en,
    et
  }

  dayjs.locale(dayjsLocaleMapping[locale.value as keyof typeof dayjsLocaleMapping])

  const capitalizeFirstLetter = (text: string) => {
    if (!text) {
      return ''
    }

    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  interface IModifiedEvent extends IEventData {
    slug: string
    mobileTitle: string
    year: string
    id: number
  }

  interface IGroupedEvents {
    [key: string]: IModifiedEvent[]
  }

  const groupedEvents = computed((): IGroupedEvents => {
    const futureEvents = props.events
    let eventIndex = 0

    if (!futureEvents) return {}

    return groupBy(
      Object.keys(futureEvents).flatMap((year: string) => {
        return futureEvents[year].map((event: InvestorEvent) => ({
          cardText: event.content.cardText,
          eventType: event.content.eventType,
          cardSubText: event.content.cardSubText,
          slug: event.slug,
          date: event.content.date,
          hasVideoRecording: event.content.hasVideoRecording,
          mobileTitle: capitalizeFirstLetter(dayjs(event.content.date).format('MMMM YYYY')),
          year,
          id: eventIndex++
        }))
      }),
      ({ date }) => capitalizeFirstLetter(dayjs(date).format('MMMM YYYY'))
    )
  })

  const currentItemIndex = ref(0)

  const scrollToItem = (index: number) => {
    const container = document.querySelector('[data-event-slider]')
    const item = container && container.querySelector(`[data-event-slider-item="${index}"]`)

    if (item && index >= 0) {
      const containerRect = container.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()

      const scrollPosition = itemRect.left - containerRect.left + container.scrollLeft

      container.scrollTo({
        left: scrollPosition - 24,
        behavior: 'smooth'
      })
    }
  }

  const currentSlideDirection = ref('right')

  const totalItemCount = computed(() => {
    return Object.keys(groupedEvents.value).reduce((total, year) => total + groupedEvents.value[year].length, 0)
  })

  const { isDesktop } = useBreakpoints()

  const goToNextItem = () => {
    const lastItemIndex = isDesktop.value ? totalItemCount.value - 2 : totalItemCount.value - 1
    if (currentItemIndex.value === lastItemIndex) {
      return
    }

    currentItemIndex.value += 1
    currentSlideDirection.value = 'right'
    scrollToItem(currentItemIndex.value)
  }

  const goToPreviousItem = () => {
    if (currentItemIndex.value === 0) {
      return
    }

    currentItemIndex.value -= 1
    currentSlideDirection.value = 'left'
    scrollToItem(currentItemIndex.value)
  }

  const visibleItem = computed(() =>
    Object.keys(groupedEvents.value)
      .flatMap((year) => groupedEvents.value[year])
      .find((_item, index) => index === currentItemIndex.value)
  )

  const showNavigationButtons = computed(() => (isDesktop.value ? totalItemCount.value > 2 : totalItemCount.value > 1))
  const isForwardButtonDisabled = computed(
    () => currentItemIndex.value === (isDesktop.value ? totalItemCount.value - 2 : totalItemCount.value - 1)
  )
</script>

<template>
  <div class="flex flex-col gap-6 rounded-2xl bg-grey px-4 py-6 lg:!px-8 lg:!py-10">
    <div
      v-if="showNavigationButtons"
      class="flex items-center justify-between md:justify-center md:gap-12 md:self-center lg:order-2 lg:justify-end lg:gap-4 lg:!self-end"
    >
      <button
        class="hover:border-bt-brand-dark-blue group flex size-12 items-center justify-center rounded-full border border-border bg-white p-3 disabled:border-none disabled:bg-disabled"
        :disabled="currentItemIndex === 0"
        @click="goToPreviousItem"
      >
        <i class="md-icon md-24 text-bt-blue-ui group-disabled:text-border">chevron_left</i>
      </button>

      <span
        v-if="visibleItem"
        class="text-bt-blue-dark text-subtitle-1 lg:hidden"
      >
        {{ visibleItem.mobileTitle }}
      </span>
      <button
        class="hover:border-bt-brand-dark-blue group flex size-12 items-center justify-center rounded-full border border-border bg-white p-3 disabled:border-none disabled:bg-[#F4F4F5]"
        :disabled="isForwardButtonDisabled"
        @click="goToNextItem"
      >
        <i class="md-icon md-24 text-bt-blue-ui group-disabled:text-border">chevron_right</i>
      </button>
    </div>
    <div
      class="flex max-w-280 gap-6 overflow-x-hidden"
      data-event-slider
    >
      <template v-if="isDesktop">
        <div
          v-for="(month, title, index) in groupedEvents"
          :key="title"
          class="flex gap-6"
        >
          <div class="flex flex-col gap-7">
            <span class="text-bt-blue-dark text-subtitle-1">
              {{ title }}
            </span>
            <div class="flex lg:gap-6">
              <EventCard
                v-for="event in month"
                :key="event.slug"
                class="flex-shrink-0 lg:!w-90"
                :data-event-slider-item="event.id"
                :card-sub-text="event.cardSubText || ''"
                :card-text="event.cardText || ''"
                :event-type="event.eventType || ''"
                :has-video-recording="event.hasVideoRecording"
                @click="emit('open', event.slug)"
              />
            </div>
          </div>
          <div
            v-if="isDesktop && index !== Object.keys(groupedEvents).length - 1"
            class="h-full w-px bg-divider"
          />
        </div>
      </template>
      <template v-else-if="visibleItem">
        <Transition
          :name="`slide-${currentSlideDirection}`"
          mode="out-in"
        >
          <EventCard
            :key="visibleItem.id"
            :data-event-slider-item="visibleItem.id"
            :card-sub-text="visibleItem.cardSubText || ''"
            :card-text="visibleItem.cardText || ''"
            :event-type="visibleItem.eventType || ''"
            :has-video-recording="visibleItem.hasVideoRecording"
            @click="emit('open', visibleItem.slug)"
          />
        </Transition>
      </template>
    </div>
  </div>
</template>

<style scoped>
  .slide-left-enter-active,
  .slide-left-leave-active,
  .slide-right-enter-active,
  .slide-right-leave-active {
    transition: transform 0.25s cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  .slide-right-enter-from {
    transform: translateX(100%);
  }
  .slide-right-leave-to {
    transform: translateX(-100%);
  }

  .slide-left-enter-from {
    transform: translateX(-100%);
  }
  .slide-left-leave-to {
    transform: translateX(100%);
  }
</style>
